import { Dispatch, FC, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, IconButton, Svg, Typography } from '@altermaind/web/ui';
import aiPlayGround from '@/_shared/assets/documents/AI_Playground_brochure_Slash_HR_v01.pdf';
import { downloadFile } from '@/_shared/helpers/download';
import { useDialog } from '@/_shared/hooks';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { ContentLike, useContentsLikes } from '../../../../contentsLikes/state';
import { CardItem } from '../../../state';

type Props = {
  item: CardItem;
  setMetricsMap?: Dispatch<SetStateAction<CardItem[]>>;
};

export const CourseDetailsDialog: FC<Props> = ({ item, setMetricsMap }) => {
  const t = useTranslations('aiPlayground.learning.landingPlayground.courses.detailsDialog');
  const { closeDialog, Dialog, isOpen } = useDialog('courseDetails');

  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();

  const { insertContentLike, removeContentLike } = useContentsLikes();

  const [internalInterested, setInternalInterested] = useState(item.hasInterestBadge || false);
  const [internalContentHash, setInternalContentHash] = useState(item.contentHash || '');

  const handleRemoveContentLike = useCallback(
    (metric: string) => {
      setInternalContentHash('');
      setInternalInterested(false);
      if (setMetricsMap) {
        setMetricsMap(prevMetrics =>
          prevMetrics.map(metricItem =>
            metricItem.title === metric
              ? {
                  ...metricItem,
                  hasInterestBadge: false,
                  contentHash: '',
                }
              : metricItem,
          ),
        );
      }
    },
    [setMetricsMap],
  );

  const handleInsertContentLike = useCallback(
    (metric: string, contentHash: string) => {
      setInternalContentHash(contentHash);
      setInternalInterested(true);
      if (setMetricsMap) {
        setMetricsMap(prevMetrics =>
          prevMetrics.map(metricItem =>
            metricItem.title === metric
              ? {
                  ...metricItem,
                  hasInterestBadge: true,
                  contentHash,
                }
              : metricItem,
          ),
        );
      }
    },
    [setMetricsMap],
  );

  const onClickLike = useCallback(
    (metric: string, currentContentHash: string) => {
      if (currentContentHash !== '') {
        removeContentLike(currentContentHash, () => handleRemoveContentLike(metric), true);
      } else {
        insertContentLike(
          {
            contentType: ContentLike.AI_PLAYGROUND_CARD,
            metadata: {
              metric,
            },
          },
          (contentHash: string) => handleInsertContentLike(metric, contentHash),
          true,
        );
      }
    },
    [handleInsertContentLike, handleRemoveContentLike, insertContentLike, removeContentLike],
  );

  const Header = useMemo(() => {
    return (
      <Box
        px={genericResponsiveValue({
          breakPoints: { desktop: 'xs' },
          defaultValue: 'xs',
        })}
        py={genericResponsiveValue({
          breakPoints: { desktop: 'xs' },
          defaultValue: 'xl',
        })}
        display='flex'
        borderRadius='m'
        alignItems='center'
        flexDirection='row'
        background={
          isDesktop
            ? `linear-gradient(to bottom, ${item.color1}, transparent)`
            : `linear-gradient(
        to bottom,
        ${item.color1},
        transparent
      ),
      conic-gradient(from 120deg at 25% 40%, ${item.color2} 0deg, ${item.color1} 300deg)`
        }
        position='relative'
      >
        <Box
          display={isDesktop ? 'block' : 'none'}
          borderRadius='l'
          width='180px'
          height='180px'
          background={`conic-gradient(from 120deg at 25% 40%, ${item.color2} 0deg, ${item.color1} 300deg)`}
        />
        <Box
          ml={genericResponsiveValue({
            breakPoints: { desktop: 'l' },
            defaultValue: 'none',
          })}
          textAlign='left'
        >
          <Typography variant='p2/light'>{t('label')}</Typography>
          <Typography variant='h1'>{item.title}</Typography>
        </Box>
        <Box
          display='flex'
          alignItems='center'
          px='xxs'
          py='xxs'
          borderRadius='xl'
          backgroundColor='white'
          position='absolute'
          top='s'
          right='xxxl'
          mt='xxxs'
          onClick={() => onClickLike(item.title, internalContentHash || '')}
          style={{ cursor: 'pointer' }}
          zIndex={999999}
        >
          {internalInterested ? (
            <Svg name='like' fill='primary' size='xs' />
          ) : (
            <Svg name='likeEmpty' fill='primary' size='xs' />
          )}
        </Box>
      </Box>
    );
  }, [
    genericResponsiveValue,
    internalContentHash,
    internalInterested,
    isDesktop,
    item.color1,
    item.color2,
    item.title,
    onClickLike,
    t,
  ]);

  return (
    <Dialog
      header={Header}
      headerStyle={{ mt: 'none', mb: 'none' }}
      maxWidth='1000px'
      open={isOpen}
      title={undefined}
      onClose={closeDialog}
      closeButton
    >
      <Box width='100%'>
        <Box mb='xs' width='100%'>
          <Box>
            <Typography variant='h2'>{t('description')}</Typography>
            <Typography variant='p3/light' mt='xs'>
              {item.longDescription}
            </Typography>
          </Box>
          <Box>
            <IconButton
              color='primary/interactive'
              variant='positive'
              mt='s'
              prefixSvg='download'
              svgStyle={{ fill: 'primary', size: 'xs' }}
              textStyle={{ ml: 'xxxs' }}
              onClick={() => downloadFile(aiPlayGround, 'AI Playground Slash')}
            >
              {t('buttonLabel')}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
